export interface OpenedFile {
  handle: FileSystemFileHandle | null;
  name: string;
}

const ACCEPT = { 'text/markdown': ['.md', '.markdown', '.mdx', '.txt'] };

export async function openFile(): Promise<{ file: OpenedFile; content: string } | null> {
  if ('showOpenFilePicker' in window) {
    try {
      const [handle] = await (window as any).showOpenFilePicker({
        types: [{ description: 'Markdown', accept: ACCEPT }],
        multiple: false,
      });
      const f = await handle.getFile();
      return { file: { handle, name: f.name }, content: await f.text() };
    } catch {
      return null;
    }
  }
  return openViaInput();
}

export async function saveFile(file: OpenedFile, content: string): Promise<boolean> {
  if (!file.handle) return false;
  try {
    const writable = await (file.handle as any).createWritable();
    await writable.write(content);
    await writable.close();
    return true;
  } catch {
    return false;
  }
}

export async function saveAs(content: string, suggestedName = 'untitled.md'): Promise<OpenedFile | null> {
  if ('showSaveFilePicker' in window) {
    try {
      const handle = await (window as any).showSaveFilePicker({
        suggestedName,
        types: [{ description: 'Markdown', accept: ACCEPT }],
      });
      const writable = await handle.createWritable();
      await writable.write(content);
      await writable.close();
      return { handle, name: handle.name };
    } catch {
      return null;
    }
  }
  download(content, suggestedName);
  return { handle: null, name: suggestedName };
}

function download(content: string, name: string) {
  const blob = new Blob([content], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function openViaInput(): Promise<{ file: OpenedFile; content: string } | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.md,.markdown,.mdx,.txt';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return resolve(null);
      resolve({ file: { handle: null, name: f.name }, content: await f.text() });
    };
    input.click();
  });
}

export function hasFSA(): boolean {
  return 'showOpenFilePicker' in window;
}
